import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '@/store/store';
import { fromCrontab } from '@/utils/utils';
import { hubClient } from '@tfpl/api-client';
import { getConfig } from '@/config';

export interface Crontab {
  weekday: number;
  time: string;
}

export interface Settings {
  absenceApproverId: number;
  feedbackCrontab: Crontab;
  slackAbsenceChannel: string;
  slackErrorChannel: string;
  slackSchedulerChannel: string;
  maxWorklogHours: number;
  merchEmailReceiver: string;
  merchProducts: string;
  // Absence advance-notice rules. Read-only — fixed in the backend, served here so the absence
  // form does not have to duplicate the thresholds and drift from the server's verdict.
  absenceAdvanceLong: number;
  absenceAdvanceShort: number;
  absenceLongThresholdWorkingDays: number;
  absenceHomeOfficeWeeklyCap: number;
  absenceExcludedTypes: string[];
}

export const BLANK_SETTINGS = {
  absenceApproverId: 0,
  feedbackCrontab: {
    weekday: 0,
    time: '',
  },
  slackAbsenceChannel: '',
  slackErrorChannel: '',
  slackSchedulerChannel: '',
  maxWorklogHours: 0,
  merchEmailReceiver: '',
  merchProducts: '',
  // Mirrors ABSENCE_RULES in tf-hub-server. Zeroes would make every request look on time until the
  // settings arrive — the form would quietly stop warning and stop requiring a reason whenever the
  // fetch is slow or fails, while the server still rejects the request.
  absenceAdvanceLong: 30,
  absenceAdvanceShort: 7,
  absenceLongThresholdWorkingDays: 3,
  absenceHomeOfficeWeeklyCap: 2,
  absenceExcludedTypes: ['ILLNESS', 'WORK_TRAVEL'] as string[],
};

@Module({ name: 'SettingsModule', store, dynamic: true, namespaced: true })
export class SettingsModule extends VuexModule {
  /**************************************************************************
   * STATE
   *************************************************************************/
  private _loading = false;
  private _error = false;
  private _settings: Settings = { ...BLANK_SETTINGS };

  /**************************************************************************
   * GETTERS
   *************************************************************************/
  get settings(): Settings {
    return this._settings;
  }

  get loading(): boolean {
    return this._loading;
  }

  get error(): boolean {
    return this._error;
  }

  /**************************************************************************
   * MUTATIONS
   *************************************************************************/
  @Mutation
  clearSettings(): void {
    this._settings = { ...BLANK_SETTINGS };
  }

  @Mutation
  setSettings(settings: Settings): void {
    this._settings = settings;
  }

  @Mutation
  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  @Mutation
  setError(error: boolean): void {
    this._error = error;
  }

  /**************************************************************************
   * ACTIONS
   *************************************************************************/
  @Action({ rawError: true })
  public async fetchSettings(): Promise<void> {
    this.setLoading(true);
    try {
      const { data: response } = await hubClient.V3SettingsService.getSettingsV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        throwOnError: true,
      });
      const settings: Settings = {
        absenceApproverId: response.data.backupAbsenceApprover.id,
        feedbackCrontab: fromCrontab(response.data.feedbackCrontab),
        slackAbsenceChannel: response.data.slackAbsenceChannel,
        slackErrorChannel: response.data.slackErrorChannel,
        slackSchedulerChannel: response.data.slackSchedulerChannel,
        maxWorklogHours: response.data.maxWorklogHours,
        merchEmailReceiver: response.data.merchEmailReceiver,
        merchProducts: response.data.merchProducts,
        absenceAdvanceLong: response.data.absenceAdvanceLong,
        absenceAdvanceShort: response.data.absenceAdvanceShort,
        absenceLongThresholdWorkingDays: response.data.absenceLongThresholdWorkingDays,
        absenceHomeOfficeWeeklyCap: response.data.absenceHomeOfficeWeeklyCap,
        absenceExcludedTypes: response.data.absenceExcludedTypes,
      };
      this.setSettings(settings);
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }
}
export default getModule(SettingsModule);
